import { GPC_METHODOLOGY_LOOKUP_MAP } from "./methodology-lookup";
/**
 * GPC Waste sector — activity-based emission factors. Waste is one of the GPC
 * calculated sectors; this module carries the data, wired together in
 * {@link WASTE_FACTORS}.
 *
 * Method (v1, "linear" tier):
 *   emission [kg CO₂e] = activity × factor
 * where activity is in tonnes (solid fractions) or m³ of treated wastewater (Abwasser), and
 * the factor below is the *combined* CO₂e factor in **kg CO₂e per unit** — it
 * already bundles each gas's per-activity yield × that gas's GWP. We store the
 * factor in kg-per-unit (not t-per-unit) so the engine's generic
 * `activity × factor` lands in the same kg-CO₂e scale as the energy sectors
 * (which compute Joules × kg/J = kg). Display layers convert kg → t.
 *
 *
 * Default factor values derive from IPCC 2006 Guidelines Vol. 5 (Waste) Tier-1
 * defaults, combined with AR6 GWP.
 *
 * Modeling choices baked into the defaults:
 *   - Landfill (LANDFILLS): methane-commitment approach, no gas capture,
 *     biogenic CH₄ GWP 27. Glass/metal/inert fractions are omitted (≈0).
 *   - Composting (BIOLOGICAL_WASTE_TREATMENT): IPCC default CH₄ 4 g/kg +
 *     N₂O 0.24 g/kg wet weight. Only the organic fraction applies.
 *   - Incineration (WASTE_INCINERATION): fossil CO₂ (only the fossil-carbon
 *     fractions — plastics/textiles/mixed — since biogenic CO₂ is an uncounted
 *     memo item) PLUS N₂O. N₂O forms from combustion regardless of carbon
 *     origin, so IPCC attributes it per tonne of waste burned; every incinerated
 *     fraction — biogenic ones included — therefore carries the N₂O term, even
 *     where its CO₂ is not counted. CH₄ from controlled incineration is ≈0 and
 *     omitted.
 *   - Wastewater (WASTEWATER): per-m³-of-treated-wastewater CH₄ (treatment) + N₂O
 *     (effluent/plant). N₂O follows the IPCC nitrogen-load route; CH₄ assumes
 *     partial management (MCF ≈ 0.04). See WASTE_DEFAULT_FACTORS for the math.
 */
/** IPCC AR6 100-year global warming potentials. Constants — never user-editable. */
export const WASTE_GWP_AR6 = {
    CO2: 1,
    CH4_FOSSIL: 29.8,
    CH4_BIOGENIC: 27,
    N2O: 273,
};
/**
 * The greenhouse gases the waste factor table breaks down into — the per-gas
 * editing dimension. Each (fraction × pathway) cell carries a CO₂e contribution
 * for the gases it emits (a subset of these).
 */
export const WASTE_FACTOR_GASES = ["CH4", "N2O", "CO2_FOSSIL"];
export const WASTE_GAS_GWP = {
    CH4: WASTE_GWP_AR6.CH4_BIOGENIC,
    N2O: WASTE_GWP_AR6.N2O,
    CO2_FOSSIL: WASTE_GWP_AR6.CO2,
};
/** Short gas labels for the per-gas breakdown rows in the UI. */
const WASTE_GAS_LABEL = {
    CH4: "CH₄",
    N2O: "N₂O",
    CO2_FOSSIL: "fossiles CO₂",
};
const WASTE_ACTIVITY_UNIT_LABEL = {
    t: "t",
    m3: "m³",
};
const WASTE_FACTOR_UNIT_LABEL = {
    t: "tCO₂äq/t",
    m3: "tCO₂äq/m³",
};
const { WASTE } = GPC_METHODOLOGY_LOOKUP_MAP.sectors;
const WASTE_ROW_UNIT_BY_KIND = {
    ORGANIC: "t",
    PAPER: "t",
    WOOD: "t",
    TEXTILES: "t",
    PLASTICS: "t",
    RESIDUAL_WASTE: "t",
    WASTEWATER_VOLUME: "m3",
};
/**
 * 2D default factor table, per gas in **kg CO₂e per unit** (each value is the
 * gas's per-activity mass already × its AR6 GWP). A present entry means the cell
 * is applicable (the user may enter activity there); an absent entry means the
 * (fraction × pathway) combination is not modeled and the cell is read-only in
 * the UI. The combined factor recompute uses is the sum of the per-gas
 * contributions; the gas MASS shown in the Emissionen breakdown is each
 * contribution ÷ that gas's GWP. Keyed `${fractionKind}|${subsectorKind}`.
 */
const WASTE_DEFAULT_FACTORS = new Map([
    // --- Mülldeponien (landfill), methane commitment, biogenic CH₄ × 27 (no N₂O) ---
    ["ORGANIC|LANDFILLS", { CH4: 1200 }],
    ["PAPER|LANDFILLS", { CH4: 3200 }],
    ["WOOD|LANDFILLS", { CH4: 3500 }],
    ["TEXTILES|LANDFILLS", { CH4: 1950 }],
    ["RESIDUAL_WASTE|LANDFILLS", { CH4: 1000 }],
    // --- Biologische Abfallbehandlung (composting), IPCC 2006 Table 8.3 wet-weight
    // (organic waste is high-moisture): CH₄ 4 g/kg × 27 + N₂O 0.24 g/kg × 273 → 108 + 65.52 ---
    ["ORGANIC|BIOLOGICAL_WASTE_TREATMENT", { CH4: 108, N2O: 65.52 }],
    // --- Müllverbrennung (incineration) ---
    // Fossil CO₂ (fossil-carbon fractions only; biogenic CO₂ is an uncounted memo
    // item) plus N₂O on every burned fraction. N₂O default 0.05 kg/t (IPCC 2006
    // Guidelines Vol. 5 Table 5.6, continuous-stoker MSW) × AR6 GWP 273 = 13.65 kg
    // CO₂e/t. CH₄ from controlled incineration is ≈0 and omitted.
    ["PLASTICS|WASTE_INCINERATION", { CO2_FOSSIL: 2750, N2O: 13.65 }],
    ["TEXTILES|WASTE_INCINERATION", { CO2_FOSSIL: 900, N2O: 13.65 }],
    ["RESIDUAL_WASTE|WASTE_INCINERATION", { CO2_FOSSIL: 900, N2O: 13.65 }],
    // Biogenic fractions: their combustion CO₂ is a memo item (uncounted), but the
    // N₂O still counts — so the incineration cell is applicable for N₂O alone.
    ["ORGANIC|WASTE_INCINERATION", { N2O: 13.65 }],
    ["PAPER|WASTE_INCINERATION", { N2O: 13.65 }],
    ["WOOD|WASTE_INCINERATION", { N2O: 13.65 }],
    // --- Abwasser (wastewater), per m³ of treated wastewater ---
    // Forward chain (gas mass → CO₂e), per m³:
    //   N₂O = N-load 0.05 kg N/m³ × EF 0.005 × 44/28 = 0.000393 kg → × 273 ≈ 0.11
    //   CH₄ = BOD 0.3 kg/m³ × Bo 0.6 × MCF 0.04   = 0.00704 kg → ×  27 ≈ 0.19
    // Combined ≈ 0.30 kg CO₂e/m³. N₂O is the IPCC nitrogen-load route; CH₄ assumes
    // partial management (MCF ≈ 0.04) — see WASTE_DEFAULT_PROVENANCE.
    ["WASTEWATER_VOLUME|WASTEWATER", { CH4: 0.19, N2O: 0.11 }],
]);
// Documented IPCC 2006 Guidelines Vol. 5 (Waste) Tier-1 default yield; CO₂e uses
// the AR6 GWP. Only the composting CH₄/N₂O and incineration N₂O yields are
// genuinely sourced this way.
const PROVENANCE_IPCC_2006 = {
    dataSource: "IPCC 2006 GL",
    referenceYear: 2006,
    quality: "L",
};
// ARK modeling assumption — a plausible default not traceable to a published
// table (textile/mixed incineration fossil CO₂, wastewater CH₄ via an assumed MCF).
// Labelled honestly so the UI does not over-claim an IPCC provenance.
const PROVENANCE_ESTIMATE = {
    dataSource: "Annahme",
    referenceYear: null,
    quality: "L",
};
/**
 * Provenance of the default yields, looked up most-specific-first:
 *   1. `${fractionKind}|${subsectorKind}|${gas}` — a fraction-specific override,
 *   2. `${subsectorKind}|${gas}` — the pathway-wide default.
 * Provenance is uniform across fractions for most pathways, so the pathway-level
 * key carries them; incineration fossil CO₂ is the exception — it is genuine
 * IPCC for plastics (the per-tonne C content reproduces the IPCC formula) but a
 * coarse assumption for the mixed/textile fractions, so those carry a
 * fraction-specific key. Absent ⇒ that gas is not emitted in that pathway. All
 * defaults are quality "L": generic defaults / estimates with no city-specific
 * adaptation sit at the bottom of the GPC ladder.
 */
const WASTE_DEFAULT_PROVENANCE = new Map([
    // Landfill CH₄ reproduces the IPCC FOD methane-commitment (DOC% × 3.0 kg/t),
    // so it is genuine IPCC, not an ARK assumption.
    ["LANDFILLS|CH4", PROVENANCE_IPCC_2006],
    ["BIOLOGICAL_WASTE_TREATMENT|CH4", PROVENANCE_IPCC_2006],
    ["BIOLOGICAL_WASTE_TREATMENT|N2O", PROVENANCE_IPCC_2006],
    ["WASTE_INCINERATION|N2O", PROVENANCE_IPCC_2006],
    // Incineration fossil CO₂: plastics' 2750 kg/t is the exact IPCC Table 2.4
    // result (dm 100 % · C 75 % · FCF 100 % · 44/12), so it is genuine IPCC. The
    // textile/mixed fractions use a coarse blended value — an ARK assumption.
    ["PLASTICS|WASTE_INCINERATION|CO2_FOSSIL", PROVENANCE_IPCC_2006],
    ["WASTE_INCINERATION|CO2_FOSSIL", PROVENANCE_ESTIMATE],
    // Wastewater N₂O follows the IPCC nitrogen-load route (N-load × EF × 44/28);
    // CH₄ uses an assumed partial-management MCF, so it stays an ARK assumption.
    ["WASTEWATER|CH4", PROVENANCE_ESTIMATE],
    ["WASTEWATER|N2O", PROVENANCE_IPCC_2006],
]);
/**
 * The Waste calculated-sector config — drives recompute and the frontend grids.
 * Scope 1 + Scope 3 (each fraction is reportable as treated in-boundary or
 * exported); the factor table is scope-independent.
 */
export const WASTE_FACTORS = {
    key: "WASTE",
    gases: WASTE_FACTOR_GASES,
    fgasSpecies: [],
    gasGwp: WASTE_GAS_GWP,
    gasLabel: WASTE_GAS_LABEL,
    scopes: [1, 3],
    allowsNegativeFactors: false,
    activityUnitLabel: WASTE_ACTIVITY_UNIT_LABEL,
    factorUnitLabel: WASTE_FACTOR_UNIT_LABEL,
    rowUnitByKind: WASTE_ROW_UNIT_BY_KIND,
    defaultFactors: WASTE_DEFAULT_FACTORS,
    defaultProvenance: WASTE_DEFAULT_PROVENANCE,
    categoryKindById: new Map(Object.entries(WASTE.energySources).map(([kind, source]) => [source.id, kind])),
    subsectorKindById: new Map(Object.entries(WASTE.subsectors).map(([kind, subsector]) => [subsector.id, kind])),
};
